/**
 * @file category-row-meta.ts
 * @description Тексты строки дерева категорий (phase19.31, образец (б)):
 * вид товара словами, публикация словами, пункты меню «⋯».
 *
 * Вид товара (W22, phase19.22) — три состояния: свой вид, «как у родителя
 * (Пищевой)», «вид не задан» (янтарным, если в категории есть товары).
 * Символов «↳» и значков нет — только слова.
 */

import { BadgeCheck, Eye, FolderPlus, Pencil, Trash2 } from "lucide-react";

import type { RowAction } from "../../ui/RowActionsMenu";
import type { CatalogCategory } from "../../../lib/api-catalog";
import type { ProductKind } from "../../../lib/api-product-kinds";
import {
  CATEGORY_PUBLISH_ACTION_LABEL,
  categoryPublishMode,
  type CategoryPublishMode,
} from "../../../lib/catalog-status";

export interface KindCaption {
  text: string;
  /** Янтарный тон: у товаров категории вид не определяется. */
  attention: boolean;
}

/** Вид товара категории словами; `null` — у системных категорий вида не бывает. */
export function categoryKindCaption(category: CatalogCategory, kindsById: Map<string, ProductKind>): KindCaption | null {
  if (category.shopId === null) return null;
  const own = category.productKindId ? kindsById.get(category.productKindId) : undefined;
  if (own) return { text: own.name, attention: false };
  if (category.inheritedKind) return { text: `как у родителя (${category.inheritedKind.name})`, attention: false };
  if ((category.productsCount ?? 0) > 0) return { text: "вид не задан", attention: true };
  return null;
}

interface ActionHandlers {
  onAddChild: (c: CatalogCategory) => void;
  onEdit: (c: CatalogCategory) => void;
  onMarking: (c: CatalogCategory) => void;
  onPublish: (c: CatalogCategory, mode: CategoryPublishMode) => void;
  onDelete: (c: CatalogCategory) => void;
}

const PUBLISH_ORDER: CategoryPublishMode[] = ["yes", "inherit", "no"];

/**
 * Меню «⋯» строки: подкатегория, изменить, публикация (три пункта — текущий
 * выключен), маркировка «Честный знак», после линии — удалить. Системной
 * категории — только «Подкатегория»: её нельзя менять (catalog-taxonomy.md).
 */
export function categoryRowActions(c: CatalogCategory, h: ActionHandlers): RowAction[] {
  const actions: RowAction[] = [{ key: "child", label: "Подкатегория", icon: FolderPlus, run: () => h.onAddChild(c) }];
  if (c.isSystemRoot) return actions;
  const current = categoryPublishMode(c);
  actions.push({ key: "edit", label: "Изменить", icon: Pencil, run: () => h.onEdit(c) });
  for (const mode of PUBLISH_ORDER) {
    actions.push({
      key: `publish-${mode}`,
      label: CATEGORY_PUBLISH_ACTION_LABEL[mode],
      icon: Eye,
      disabled: mode === current,
      run: () => h.onPublish(c, mode),
    });
  }
  actions.push({ key: "marking", label: "Маркировка «Честный знак»", icon: BadgeCheck, run: () => h.onMarking(c) });
  actions.push({ key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => h.onDelete(c) });
  return actions;
}
